/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-definitions */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { baseUrl } from '../../main/client';
import { ApiError, type ApiErrorResponse, UnauthorizedError } from '../errors/ApiError';

/**
 * Parses an error response and throws the appropriate ApiError.
 */
async function handleErrorResponse(result: Response): Promise<never> {
    let body: ApiErrorResponse | undefined;
    try {
        body = await result.json();
    } catch {
        // Response body is not JSON
    }

    if (body?.error && body?.code) {
        throw ApiError.fromResponse(body, result.status);
    }

    throw new ApiError(
        body?.error ?? `Request failed with status ${result.status}`,
        body?.code ?? 'unknownError',
        undefined,
        undefined,
        result.status
    );
}

export type DataLoading = {
    loading: true;
    data: undefined;
    error: undefined;
};

export type DataError = {
    loading: undefined;
    data: undefined;
    error: Error;
};

export type DataComplete<R> = {
    loading: false;
    data: R;
    error: undefined;
};

type AsyncFn = (...args: any[]) => Promise<unknown>;

type Pop<T extends any[]> = T extends [...infer U, any] ? U : never;

export function createServerHook<T extends AsyncFn>(functionName: string) {
    return () =>
        useMemo(() => {
            const token = localStorage.getItem('token');
            return async (
                ...params: Pop<Parameters<T>>
            ): Promise<Awaited<ReturnType<T>>> => {
                const result = await fetch(`${baseUrl}/api/${functionName}`, {
                    method: 'POST',
                    headers: {
                        Authorization: `bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(params),
                });

                if (result.status === 401) {
                    // Only reload if there was a token (user session expired)
                    // Don't reload if token is already gone (user already logged out)
                    if (localStorage.getItem('token')) {
                        localStorage.removeItem('token');
                        window.location.reload();
                    }
                    throw new UnauthorizedError();
                }

                if (result.status !== 200) {
                    await handleErrorResponse(result);
                }

                return result.json() as Awaited<ReturnType<T>>;
            };
        }, []);
}

export function createServerQueryHook<
    T extends (...args: any[]) => Promise<unknown>,
>(functionName: string) {
    return ({
        params,
        skip = false,
        pollInterval,
    }: {
        params: Pop<Parameters<T>>;
        pollInterval?: number;
        skip?: boolean;
    }) => {
        const active = useRef(true);
        const [state, setState] = useState<
            DataLoading | DataError | DataComplete<Awaited<ReturnType<T>>>
        >({
            data: undefined,
            loading: true,
            error: undefined,
        });

        // Store params in a ref to avoid recreating reload on every render
        const paramsRef = useRef(params);
        paramsRef.current = params;

        // Create a stable key for params changes using useMemo
        const paramsKey = useMemo(() => JSON.stringify(params), [params]);

        const reload = useCallback(
            (completeReload = false) => {
                (async () => {
                    if (completeReload) {
                        setState({
                            loading: true,
                            data: undefined,
                            error: undefined,
                        });
                    }

                    const token = localStorage.getItem('token');
                    const result = await fetch(
                        `${baseUrl}/api/${functionName}`,
                        {
                            method: 'POST',
                            headers: {
                                Authorization: `bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(paramsRef.current),
                        }
                    );

                    if (!active.current) {
                        return;
                    }

                    if (result.status === 401) {
                        // Only reload if there was a token (user session expired)
                        // Don't reload if token is already gone (user already logged out)
                        if (localStorage.getItem('token')) {
                            localStorage.removeItem('token');
                            window.location.reload();
                        }
                        return;
                    }

                    if (result.status !== 200) {
                        await handleErrorResponse(result);
                    }

                    setState({
                        loading: false,
                        data: await result.json(),
                        error: undefined,
                    });
                })().catch(error => {
                    setState({
                        loading: undefined,
                        data: undefined,
                        error,
                    });
                });
            },
            [paramsKey]
        );

        useEffect(() => {
            if (skip) {
                return () => {};
            }

            reload();

            const interval = pollInterval
                ? setInterval(reload, pollInterval)
                : undefined;

            return () => {
                if (interval && pollInterval) {
                    clearInterval(interval);
                }
            };
        }, [skip, reload, pollInterval]);

        useEffect(() => {
            active.current = true;
            return () => {
                active.current = false;
            };
        }, []);

        return useMemo(
            () => ({
                ...state,
                reload,
            }),
            [state, reload]
        );
    };
}
